import React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { KeyboardShortcutsContext } from '../KeyboardShortcutsProvider';
import { shortcutToString } from '../utils/shortcutToString';

type UseKeyboardShortcutConfig = {
	id: string;
	handler?: Function;
	deps?: any[];
	focusedScope?: React.MutableRefObject<HTMLElement | null>;
	disableDefault? : boolean;
};
type UseKeyboardShortcutReturnedValue = {
	keys: string;
	isActive: boolean;
	trigger: Function;
};

export function useKeyboardShortcut({
	id,
	handler,
	deps = [],
	focusedScope,
	disableDefault,
}: UseKeyboardShortcutConfig): UseKeyboardShortcutReturnedValue {
	const context = React.useContext(KeyboardShortcutsContext);

	if (context === undefined) {
		throw new Error('useKeyboardShortcut must be used within a KeyboardShortcutsProvider');
	}

	const { state, dispatch } = context;
	const { shortcuts } = state;

	const shortcutState = shortcuts[id];

	if (!shortcutState) {
		throw new Error(
			`shortcut ${id} is being used with useKeyboardShortcut, but it isn't defineds in KeyboardShortcutsProvider.`,
		);
	}

	if (shortcutState.isLocal && !focusedScope && handler) {
		throw new Error(`shortcut ${id} is a local shortcut but no "focusedScope" was supplied.`);
	}

	React.useEffect(() => {
		dispatch({ type: 'SET_IS_IN_PAGE', payload: { id, isInPage: true } });
		if (handler) {
			dispatch({ type: 'ADD_HANDLER', payload: { id, handler } });
		}
		return () => {
			dispatch({ type: 'SET_IS_IN_PAGE', payload: { id, isInPage: false } });
		};
		// eslint-disable-next-line
	}, []);

	const wrappedHandler = React.useCallback(
		event => {
			if (!handler) {
				return;
			}
			if (!disableDefault) {
				event.preventDefault();
			}
			
			if (focusedScope) {
				const activeElement = document.activeElement?.shadowRoot
					? document.activeElement.shadowRoot.activeElement
					: document.activeElement;
				if (shortcutState.triggerIfUnfocusedSingleInstance && shortcutState.instances === 1) {
					handler(event);
					return;
				} else if (focusedScope.current && activeElement && focusedScope.current.contains(activeElement)) {
					handler(event);
				}
			} else {
				handler(event);
			}
		},
		[focusedScope, handler, shortcutState.instances, shortcutState.triggerIfUnfocusedSingleInstance],
	);

	useHotkeys(
		shortcutState.keys,
		wrappedHandler,
		// @ts-ignorefconsl
		{
			enabled: shortcutState.isActive,
			// @ts-ignore
			enableOnTags:
				shortcutState.triggersInInputs || shortcutState.triggersInTextareas
					? ([]
						// @ts-ignore
						.concat(shortcutState.triggersInInputs ? ['INPUT'] : [])
						// @ts-ignore
						.concat(shortcutState.triggersInTextareas ? ['TEXTAREA'] : []) as string[])
					: undefined,
		},
		[...deps, shortcutState.isActive],
	);

	const noop = () => { };
	return {
		keys: shortcutToString(shortcutState.keys),
		isActive: shortcutState.isActive,
		trigger: shortcutState.isActive ? shortcutState.handler ?? noop : noop,
	} as UseKeyboardShortcutReturnedValue;
}
